import { useState } from "react";
import { DataTable } from "../../ui/data-table/data-table";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Eye, Funnel } from "lucide-react";
import { ExportMenu } from "../../ui/export-menu";
import { DataTableColumnHeader } from "../../ui/data-table/data-table-column-header";
import { useWholesaleABCRanking } from "../hooks/useWholesaleABCRanking";
import { WholesaleStoreFilter } from "./wholesale-storeFilter";

// Define the data type for each row
export type WholesaleAccountStoreRow = {
  ranking: string;
  skus: number;
  dailySalesCriteria: ">=0.5" | "0.2-0.5" | "<=0.2" | "Not yet sold";
  percentActiveSkus: number;
};

const criteriaOptions = [
  { label: "All", value: "" },
  { label: ">=0.5", value: ">=0.5" },
  { label: "0.2 - 0.5", value: "0.2-0.5" },
  { label: "<=0.2", value: "<=0.2" },
  { label: "Not yet sold", value: "Not yet sold" },
];

const rankingOptions = [
  { label: "All", value: "" },
  { label: "A", value: "A" },
  { label: "B", value: "B" },
  { label: "C", value: "C" },
  { label: "N/A", value: "N/A" },
];

export function WholesaleABCRankingTable() {
  // Main filter states (used for filtering)
  const [window_size, setWindowSize] = useState<number>(150);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedRankings, setSelectedRankings] = useState<string[]>([]);
  const [selectedCriteria, setSelectedCriteria] = useState<string[]>([]);

  // Modal temp states
  const [modalSelectedStores, setModalSelectedStores] = useState(selectedStores);
  const [modalSelectedRankings, setModalSelectedRankings] = useState(selectedRankings);
  const [modalSelectedCriteria, setModalSelectedCriteria] = useState(selectedCriteria);

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [rankingHeaderFilter, setRankingHeaderFilter] = useState<string>("");
  const [criteriaHeaderFilter, setCriteriaHeaderFilter] = useState<string>("");

  const { data, isLoading, error } = useWholesaleABCRanking(window_size);

  const rows = data
    ? data.flatMap(store =>
      store.buckets.map(bucket => ({
        store: store.store,
        ranking: bucket.rank,
        skus: bucket.number_of_sku,
        dailySalesCriteria: bucket.daily_sales_criteria,
        percentActiveSkus: bucket.percentage,
      }))
    )
    : [];

  const storeOptions = data
    ? Array.from(new Set(data.map(store => store.store)))
    : [];

  const filteredData = rows.filter(row =>
    (selectedStores.length === 0 || selectedStores.includes(row.store)) &&
    (selectedRankings.length === 0 || selectedRankings.includes(row.ranking)) &&
    (selectedCriteria.length === 0 || selectedCriteria.includes(row.dailySalesCriteria)) &&
    (!rankingHeaderFilter || row.ranking === rankingHeaderFilter) &&
    (!criteriaHeaderFilter || row.dailySalesCriteria === criteriaHeaderFilter)
  );

  const handleApplyFilters = () => {
    
    setSelectedStores(modalSelectedStores);
    setSelectedRankings(modalSelectedRankings);
    setSelectedCriteria(modalSelectedCriteria);
    setFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    setSelectedStores([]);
    setSelectedRankings([]);
    setSelectedCriteria([]);
    setWindowSize(100);

    setModalSelectedStores([]);
    setModalSelectedRankings([]);
    setModalSelectedCriteria([]);
    

    setRankingHeaderFilter("");
    setCriteriaHeaderFilter("");
  };

  const getActiveFiltersCount = () => {
    const modalFilters = selectedStores.length + selectedRankings.length + selectedCriteria.length;
    const headerFilters = (rankingHeaderFilter ? 1 : 0) + (criteriaHeaderFilter ? 1 : 0);
    return modalFilters + headerFilters;
  };

  const openFilterModal = () => {
   
    setModalSelectedStores(selectedStores);
    setModalSelectedRankings(selectedRankings);
    setModalSelectedCriteria(selectedCriteria);
    setFilterModalOpen(true);
  };

  // Columns for DataTable
  const columns = [
    {
      accessorKey: "store",
      header: "Store",
      enableSorting: true,
    },
    {
      accessorKey: "ranking",
      header: "Ranking",
      enableSorting: true,
    },
    {
      accessorKey: "skus",
      header: ({ column }: { column: any }) => (
        <DataTableColumnHeader
          column={column}
          title="# of SKUs"
          showSortingOptions={true}
        />
      ),
      cell: (info: any) => info.getValue(),
      filterFn: (row: any, columnId: string, filterData: string) =>
        !filterData || row.getValue(columnId) === filterData,
    },
    {
      accessorKey: "dailySalesCriteria",
      header: "Daily Sales Criteria",
      enableSorting: true,
    },
    {
      accessorKey: "percentActiveSkus",
      header: ({ column }: { column: any }) => (
        <DataTableColumnHeader
          column={column}
          title="% of Total Active SKUs"
          showSortingOptions={true}
        />
      ),
      cell: (info: any) => info.getValue(),
      filterFn: (row: any, columnId: string, filterData: string) =>
        !filterData || row.getValue(columnId) === filterData,
    },
  ];

  return (
    <>
      <SummaryHeader title="Store Overview" />
      <Card>
        <div className="flex flex-col sm:flex-row gap-2 p-4">
          <div className="flex items-center justify-between mb-4">
            <Button
              size="sm"
              onClick={openFilterModal}
              variant={getActiveFiltersCount() > 0 ? "default" : "default"}
            >
              <Funnel className="mr-1 h-4 w-4" />
              Advance Filter {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
            </Button>

            {getActiveFiltersCount() > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleResetFilters}
                className="ml-1"
              >
                Clear all filters
              </Button>
            )}
          </div>
          <div className="w-full flex justify-end gap-2">
            <Button size="sm">
              <Eye className="mr-2 h-4 w-4" />
              View All
            </Button>
            <ExportMenu
              data={filteredData}
              filename="orders_data"
              buttonSize="sm"
              label=""
            />
          </div>
        </div>
        <CardContent className="p-4">


          {isLoading ? (
            <div>Loading...</div>
          ) : error ? (
            <div className="text-red-500">Error loading data</div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              searchColumn="store"
              searchPlaceholder="Search by store..."
              initialPageSize={10}
            />
          )}
        </CardContent>
      </Card>

      {/* filter modal */}
      <WholesaleStoreFilter
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        storeOptions={storeOptions}
        rankingOptions={rankingOptions.map(opt => opt.value).filter(Boolean)}
        criteriaOptions={criteriaOptions.map(opt => opt.value).filter(Boolean)}
        windowSize={window_size}
        setWindowSize={setWindowSize}
        selectedStores={modalSelectedStores}
        setSelectedStores={setModalSelectedStores}
        selectedRankings={modalSelectedRankings}
        setSelectedRankings={setModalSelectedRankings}
        selectedCriteria={modalSelectedCriteria}
        setSelectedCriteria={setModalSelectedCriteria}
      />
    </>
  );
}